import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { logRingCentralCall, findContactByPhone } from '../utils/ringcentralCallLog'

const RingCentralContext = createContext(null)

export function RingCentralProvider({ children }) {
  const [isReady, setIsReady] = useState(false)
  const [inboundCall, setInboundCall] = useState(null)
  const [activeCall, setActiveCall] = useState(null)
  const [widgetVisible, setWidgetVisible] = useState(false)
  const activeCallRef = useRef(null)

  useEffect(() => {
    const clientId = import.meta.env.VITE_RC_CLIENT_ID
    if (!clientId) return

    const appServer = import.meta.env.VITE_RC_APP_SERVER || 'https://platform.ringcentral.com'
    const script = document.createElement('script')
    script.src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?clientId=${encodeURIComponent(clientId)}&appServer=${encodeURIComponent(appServer)}`
    script.async = true
    document.head.appendChild(script)

    const handleMessage = async (event) => {
      const data = event.data
      if (!data?.type) return

      switch (data.type) {
        case 'rc-adapter-initialized':
          setIsReady(true)
          break

        case 'rc-call-ring-notify': {
          const call = data.call || {}
          const rawNumber = call.from?.phoneNumber || ''
          const contact = await findContactByPhone(rawNumber)
          setInboundCall({ ...call, contact })
          break
        }

        case 'rc-call-start-notify':
          setActiveCall(data.call || null)
          activeCallRef.current = data.call || null
          setInboundCall(null)
          break

        case 'rc-call-end-notify': {
          const endedCall = data.call || activeCallRef.current
          setActiveCall(null)
          setInboundCall(null)
          activeCallRef.current = null
          if (endedCall) {
            try { await logRingCentralCall(endedCall) } catch (e) { console.warn('RC call log failed:', e) }
          }
          break
        }

        case 'rc-dialer-status-notify':
          if (data.open === false) setInboundCall(null)
          break
      }
    }

    window.addEventListener('message', handleMessage)

    // ─── Tag the SDK's injected widget so CSS can hide it reliably ─────────────
    // RC Embeddable injects an iframe (apps.ringcentral.com) and a wrapper
    // around it that holds the minimized chip rendered outside the iframe.
    // Tag both the iframe AND its IMMEDIATE parent (one level up only — that's
    // the SDK's wrapper, which is appended directly to <body>, not the React
    // root). A previous version walked all the way up to body's direct child
    // and accidentally tagged shared containers, blanking the page on toggle.
    const RC_HOST_PATTERN = /apps\.ringcentral\.com|ringcentral-embeddable/i
    const REACT_ROOT_IDS = new Set(['root', 'app', '__next', '__vite-root'])
    const tagRcIframe = (frame) => {
      if (!(frame instanceof HTMLIFrameElement)) return
      if (!RC_HOST_PATTERN.test(frame.src || '')) return
      frame.classList.add('chatrone-rc-element')
      // Tag the immediate parent ONLY — never tag the React root
      const parent = frame.parentElement
      if (parent && parent !== document.body && !REACT_ROOT_IDS.has(parent.id || '')) {
        parent.classList.add('chatrone-rc-element')
      }
    }
    const scanForRcIframes = (root) => {
      if (root instanceof HTMLIFrameElement) tagRcIframe(root)
      root.querySelectorAll?.('iframe').forEach(tagRcIframe)
    }
    scanForRcIframes(document.body)
    const observer = new MutationObserver(muts => {
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (n instanceof HTMLElement) scanForRcIframes(n)
        })
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      window.removeEventListener('message', handleMessage)
      observer.disconnect()
      if (document.head.contains(script)) document.head.removeChild(script)
    }
  }, [])

  const clickToDial = useCallback((phoneNumber) => {
    if (!phoneNumber) return
    const digits = phoneNumber.replace(/\D/g, '')
    // Show the widget so the user sees the call connecting
    showWidget()
    window.postMessage({ type: 'rc-adapter-new-call', phoneNumber: digits, toCall: true }, '*')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Show / hide the embedded widget. CSS in index.css watches body.rc-widget-visible
  // and toggles the SDK's iframe between display:none and a fixed popover.
  const showWidget = useCallback(() => {
    setWidgetVisible(true)
    document.body.classList.add('rc-widget-visible')
    // Un-minimize the embedded widget so it expands to the full softphone view
    window.postMessage({ type: 'rc-adapter-syncMinimized', minimized: false }, '*')
  }, [])

  const hideWidget = useCallback(() => {
    setWidgetVisible(false)
    document.body.classList.remove('rc-widget-visible')
    window.postMessage({ type: 'rc-adapter-syncMinimized', minimized: true }, '*')
  }, [])

  const toggleWidget = useCallback(() => {
    if (widgetVisible) hideWidget()
    else showWidget()
  }, [widgetVisible, showWidget, hideWidget])

  // Auto-show widget when an active call begins; auto-hide a moment after it ends
  useEffect(() => {
    if (activeCall) showWidget()
  }, [activeCall, showWidget])

  const dismissInbound = useCallback(() => setInboundCall(null), [])

  return (
    <RingCentralContext.Provider value={{
      isReady, inboundCall, activeCall, clickToDial, dismissInbound,
      widgetVisible, showWidget, hideWidget, toggleWidget,
    }}>
      {children}
    </RingCentralContext.Provider>
  )
}

export function useRingCentral() {
  return useContext(RingCentralContext)
}
